import { type ReactNode, useState } from 'react'
import { Head, useForm, usePage } from '@inertiajs/react'
import AdminLayout from '@/components/AdminLayout'
import ResearchNav from '@/components/admin/research/ResearchNav'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import ImageUpload from '@/components/ImageUpload'

type Props = {
  sponsor: {
    id: string
    name: string
    website: string | null
    industry: string | null
    notes: string | null
    logo_url: string | null
  }
}

export default function ResearchSponsorEdit({ sponsor }: Props) {
  const pageErrors = usePage().props.errors as Record<string, string | string[]> | undefined
  const [logoUploading, setLogoUploading] = useState(false)
  const { data, setData, patch, processing } = useForm({
    research_sponsor: {
      name: sponsor.name,
      website: sponsor.website ?? '',
      industry: sponsor.industry ?? '',
      notes: sponsor.notes ?? '',
      logo: null as string | null,
      remove_logo: false,
    },
  })

  const form = data.research_sponsor

  function fieldError(field: string): string | undefined {
    const err = pageErrors?.[field]
    return Array.isArray(err) ? err[0] : err
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (logoUploading) return

    patch(`/admin/research/sponsors/${sponsor.id}`)
  }

  return (
    <>
      <Head title={`Research - Edit ${sponsor.name}`} />
      <div>
        <h1 className="text-3xl font-semibold tracking-tight font-serif">Research Database</h1>
        <div className="mt-4">
          <ResearchNav />
        </div>
        <h2 className="mb-6 text-2xl font-semibold tracking-tight">Edit {sponsor.name}</h2>
        <div className="max-w-2xl">
          <Card>
            <CardHeader>
              <CardTitle>Sponsor details</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="sponsor-name">
                      Name<span className="text-destructive"> *</span>
                    </Label>
                    <Input
                      id="sponsor-name"
                      value={form.name}
                      onChange={e => setData('research_sponsor', { ...form, name: e.target.value })}
                    />
                    {fieldError('name') && (
                      <p className="text-xs text-destructive">{fieldError('name')}</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="sponsor-website">Website</Label>
                    <Input
                      id="sponsor-website"
                      type="url"
                      placeholder="https://..."
                      value={form.website}
                      onChange={e => setData('research_sponsor', { ...form, website: e.target.value })}
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="sponsor-industry">Professional Network</Label>
                  <Input
                    id="sponsor-industry"
                    placeholder="e.g. Finance, Legal, Retail"
                    value={form.industry}
                    onChange={e => setData('research_sponsor', { ...form, industry: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="sponsor-notes">Notes</Label>
                  <Textarea
                    id="sponsor-notes"
                    rows={3}
                    value={form.notes}
                    onChange={e => setData('research_sponsor', { ...form, notes: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Logo</Label>
                  <ImageUpload
                    currentUrl={sponsor.logo_url}
                    onUploadingChange={setLogoUploading}
                    onUpload={signedId => {
                      setData(current => ({
                        ...current,
                        research_sponsor: {
                          ...current.research_sponsor,
                          logo: signedId || null,
                          remove_logo: !signedId,
                        },
                      }))
                    }}
                  />
                </div>
                <div className="flex justify-end pt-2">
                  <Button type="submit" disabled={processing || logoUploading}>
                    {logoUploading ? 'Uploading logo...' : 'Save changes'}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </>
  )
}

ResearchSponsorEdit.layout = (page: ReactNode) => <AdminLayout>{page}</AdminLayout>
